import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { FRIENDS } from "@/constants/testIds";
import { toast } from "sonner";

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function InviteFriends({ roomId, playerUserIds = [] }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/friends", { headers: authHeader() });
      setFriends(r.data);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const invite = async (uid) => {
    setInviting(s => ({ ...s, [uid]: true }));
    try {
      await api.post(`/rooms/${roomId}/invite`, { user_id: uid }, { headers: authHeader() });
      toast.success("Invite sent");
      setInviting(s => ({ ...s, [uid]: "sent" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
      setInviting(s => ({ ...s, [uid]: false }));
    }
  };

  const eligible = friends.filter(f => !playerUserIds.includes(f.user_id));
  const online = eligible.filter(f => f.is_online);
  const offline = eligible.filter(f => !f.is_online);
  const ordered = [...online, ...offline];

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60 font-semibold">Invite friends</div>
        {online.length > 0 && (
          <div className="text-[10px] uppercase tracking-widest text-white/40">{online.length} online</div>
        )}
      </div>
      {loading && <div className="text-white/50 text-sm">Loading friends…</div>}
      {!loading && eligible.length === 0 && (
        <div className="text-white/50 text-sm">
          No friends to invite yet. Add some from the Friends page.
        </div>
      )}
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {ordered.map(f => {
          const state = inviting[f.user_id];
          return (
            <div key={f.user_id} className="flex items-center gap-3 bg-black/25 rounded-xl px-3 py-2">
              <div className="relative shrink-0">
                <img src={f.avatar || f.picture} alt="" className="w-9 h-9 rounded-full border border-white/10"/>
                <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-black ${f.is_online ? "bg-green-400" : "bg-gray-500"}`}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{f.name}</div>
                <div className="text-[11px] text-white/40">
                  {f.active_room ? `In a ${f.active_room.mode_label || f.active_room.mode} game` : (f.is_online ? "Online" : "Offline")}
                </div>
              </div>
              <button
                data-testid={`${FRIENDS.inviteBtnPrefix}${f.user_id}`}
                onClick={() => invite(f.user_id)}
                disabled={state === true || state === "sent"}
                className={`px-3 py-1.5 rounded-full text-xs font-bold shrink-0 ${
                  state === "sent"
                    ? "bg-white/10 text-white/60 cursor-default"
                    : "bg-[color:var(--uno-yellow)] text-black hover:brightness-110 disabled:opacity-60"
                }`}
              >
                {state === "sent" ? "Sent" : state === true ? "…" : "Invite"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
